import Idea, {AccessKind, PropertyAccessKind} from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import {formulaName} from '@/utils/utils'
import {travelsAsList} from '@/utils/propertyDefinition'

/**
 * Os exemplos que aparecem embaixo de um campo de fórmula enquanto ele está vazio, já com os nomes DA
 * COLEÇÃO quando dá (pedido do usuário: os do acesso "já aplicáveis àquele contexto" — e os das regras,
 * das opções permitidas e do cálculo passaram a ser assim). Sem campo que sirva, o de reserva; sem nem
 * esse, nada — e a linha de exemplo não aparece.
 *
 * Um dono para todos: a janela de acesso e os editores de atributo e de relacionamento leem daqui.
 */

/** O rascunho de uma propriedade, como os editores o montam para a checagem da fórmula. */
type Draft = Partial<IdeaProperty>

export type FormulaExamples = { rule: string, optionFilter: string, computed: string }

const named = (property: IdeaProperty) => formulaName(property.alias)

/** As outras propriedades da coleção — a do rascunho não se cita a si mesma. */
const othersOf = (idea: Idea, draft: Draft) => idea.properties.filter(property => property.alias !== draft.alias)

/** De um valor só, do tipo dado. */
const singleOf = (properties: IdeaProperty[], type: string) =>
    properties.filter(property => property.type === type && !property.multiple)

/** O relacionamento direto, de um valor só — o que dá para seguir com o ponto (`entrega.projeto`). */
const isSingleLink = (property: IdeaProperty) => !!property.relationship && !travelsAsList(property)

function hasWorkflow(workspace: EnhancedWorkpace, ideaAlias: string | undefined): boolean {
    const idea = ideaAlias ? workspace.ideaWith(ideaAlias) : undefined
    return !!idea && !!workspace.workflowOf(idea)
}

/** A regra: `self` é o valor do próprio campo. */
function ruleExample(draft: Draft, idea: Idea, workspace: EnhancedWorkpace): string {
    if (travelsAsList(draft as IdeaProperty)) return 'self.size() <= 3'
    if (draft.relationship && hasWorkflow(workspace, draft.relationship.idea)) return '!self._state.final'
    if (draft.type === 'text') return 'self.size() <= 50'
    if (draft.type === 'number') return 'self >= 0'
    if (draft.type === 'date') {
        const [other] = singleOf(othersOf(idea, draft), 'date')
        return `self >= ${other ? named(other) : 'today'}`
    }
    //Os demais — seleção, sim/não, pessoa, o relacionamento sem fluxo do outro lado: preenchido para terminar
    return workspace.workflowOf(idea) ? '!_state.final || filled(self)' : ''
}

/** As opções permitidas: `self` é cada opção. */
function optionFilterExample(draft: Draft, idea: Idea, workspace: EnhancedWorkpace): string {
    const relationship = draft.relationship
    if (relationship) {
        if (hasWorkflow(workspace, relationship.idea)) return '!self._state.final'
        //O destino aponta para onde esta coleção também aponta: as opções do mesmo cliente do item
        const target = workspace.ideaWith(relationship.idea)
        for (const mine of othersOf(idea, draft).filter(isSingleLink)) {
            const theirs = target && target.properties.find(property =>
                isSingleLink(property) && property.relationship!.idea === mine.relationship!.idea)
            if (theirs) return `self.${named(theirs)} == ${named(mine)}`
        }
        return ''
    }
    if (draft.type === 'choice') {
        const domain = workspace.domainWith(draft.domain)
        const [option] = domain ? domain.options : []
        return option ? `self != "${option.alias}"` : ''
    }
    if (draft.type === 'user') {
        const [role] = workspace.roles || []
        return role ? `"${role.alias}" in self.roles` : ''
    }
    return ''
}

/** O cálculo, pelo tipo que ele dá. */
function computedExample(draft: Draft, idea: Idea, workspace: EnhancedWorkpace): string {
    const others = othersOf(idea, draft)
    const relationship = draft.relationship
    if (relationship) {
        if (draft.multiple) {
            //Uma lista desta coleção com itens do destino, filtrada
            const list = others.find(property => property.relationship && travelsAsList(property)
                && property.relationship.idea === relationship.idea)
            if (!list) return ''
            const name = named(list)
            const item = name[0]
            return hasWorkflow(workspace, relationship.idea) ? `${name}.filter(${item}, !${item}._state.final)` : name
        }
        //Um passo pelo relacionamento de lá: `entrega.projeto`
        for (const via of others.filter(isSingleLink)) {
            const viaIdea = workspace.ideaWith(via.relationship!.idea)
            const hop = viaIdea && viaIdea.properties.find(property =>
                isSingleLink(property) && property.relationship!.idea === relationship.idea)
            if (hop) return `${named(via)}.${named(hop)}`
        }
        return ''
    }
    switch (draft.type) {
        case 'number': {
            const numbers = singleOf(others, 'number')
            if (numbers.length >= 2) return `${named(numbers[0])} * ${named(numbers[1])}`
            const list = others.find(property => travelsAsList(property))
            return list ? `${named(list)}.size()` : 'daysBetween(_createdAt, today)'
        }
        case 'text': {
            const texts = singleOf(others, 'text')
            return texts.length >= 2 ? `${named(texts[0])} + " · " + ${named(texts[1])}` : ''
        }
        case 'boolean': {
            const [date] = singleOf(others, 'date')
            return date ? `${named(date)} < today` : ''
        }
        //Sem fluxo, a coleção não tem situação: a data que todo item tem é a de criação
        case 'date':
            return workspace.workflowOf(idea) ? '_stateChangedAt' : '_createdAt'
    }
    return ''
}

/** Os exemplos dos três usos da fórmula numa propriedade — a regra, as opções permitidas e o cálculo. */
export function formulaExamples(draft: Draft, idea: Idea, workspace: EnhancedWorkpace): FormulaExamples {
    return {
        rule: ruleExample(draft, idea, workspace),
        optionFilter: optionFilterExample(draft, idea, workspace),
        computed: computedExample(draft, idea, workspace)
    }
}

/**
 * Um exemplo por pergunta de acesso, já com o `currentUser` (pedido do usuário), e com os nomes DESTA
 * coleção quando dá: o atributo de usuário dela, para quem vê; senão quem criou, que toda coleção tem.
 * Quem cria lê o item que se está criando — onde quem criou é sempre quem pede —, e o exemplo é pelo
 * papel do ambiente, quando ele tem algum, e senão pela conta.
 */
export function accessExamples(idea: Idea, workspace: EnhancedWorkpace): Record<AccessKind, string> {
    const ownedBy = '_createdBy == currentUser'
    return {
        visible: personOf(idea) || ownedBy,
        editable: ownedBy,
        deletable: ownedBy,
        creatable: roleOf(workspace) || 'currentUser.email.endsWith("@empresa.com")'
    }
}

/**
 * "Visível quando" e "Editável quando" de um CAMPO — pelo que é típico de campo, e não de item (pedido do
 * usuário): depender de OUTRO campo do item. O primeiro que a coleção permite; o rascunho (`draft`) fica
 * de fora, que o campo não se cita.
 *
 * Fora, de propósito, o "edita enquanto vazio" (`!filled(campo)`): ao criar, o editável é julgado sobre o
 * item como ficaria — com o campo já preenchido —, e a criação seria recusada justamente por preenchê-lo.
 */
export function fieldAccessExamples(draft: Draft, idea: Idea, workspace: EnhancedWorkpace): Record<PropertyAccessKind, string> {
    const others = othersOf(idea, draft).filter(property => !property.formula)
    return {visible: fieldVisibleExample(others, idea, workspace), editable: fieldEditableExample(others, idea, workspace)}
}

/**
 * Visível: só para uma opção de uma seleção (o CNPJ só da pessoa jurídica); depois que outro campo é
 * preenchido, de preferência um relacionamento (a Cidade depois do Estado); depois da situação inicial;
 * e, sem nada disso, por pessoa, como o do item.
 */
function fieldVisibleExample(others: IdeaProperty[], idea: Idea, workspace: EnhancedWorkpace): string {
    for (const choice of singleOf(others, 'choice')) {
        const domain = workspace.domainWith(choice.domain)
        const [option] = domain ? domain.options : []
        if (option) return `${named(choice)} == "${option.alias}"`
    }
    const before = others.find(isSingleLink) || others.find(property => !travelsAsList(property) && !property.identifier)
    if (before) return `filled(${named(before)})`
    const initial = initialStateOf(idea, workspace)
    if (initial) return `_state != "${initial.alias}"`
    return roleOf(workspace) || '_createdBy == currentUser'
}

/** Editável: só na situação inicial; até um sim/não da coleção ligar (trava depois de aprovado); ou quem criou. */
function fieldEditableExample(others: IdeaProperty[], idea: Idea, workspace: EnhancedWorkpace): string {
    const initial = initialStateOf(idea, workspace)
    if (initial) return `_state == "${initial.alias}"`
    const [flag] = singleOf(others, 'boolean')
    return flag ? `!${named(flag)}` : '_createdBy == currentUser'
}

/** A situação em que o item nasce, com fluxo — a marcada, ou a primeira. */
function initialStateOf(idea: Idea, workspace: EnhancedWorkpace): { alias: string } | undefined {
    const workflow = workspace.workflowOf(idea)
    const states = workflow ? workflow.states : []
    return states.find(state => state.initial) || states[0]
}

/** O atributo de usuário da coleção, para "a pessoa do item é quem pede" — ou nada. */
function personOf(idea: Idea): string {
    const person = idea.properties.find(property => property.type === 'user' && !property.formula)
    if (!person) return ''
    return person.multiple ? `currentUser in ${named(person)}` : `${named(person)} == currentUser`
}

/** Um papel do ambiente, para "quem tem o papel" — ou nada. */
function roleOf(workspace: EnhancedWorkpace): string {
    const [role] = workspace.roles || []
    return role ? `"${role.alias}" in currentUser.roles` : ''
}
